import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldCheck, Wallet, Vote, ArrowRight } from 'lucide-react';
import { Hero, CTABanner } from '@/components/marketing/primitives.jsx';

const subsystems = [
  {
    icon: AlertTriangle,
    code: 'RERR',
    name: 'Random Event Reporting & Response',
    tagline: 'Device-attested civic reporting with chain-of-custody from field to dispatch.',
    bullets: [
      'Priority Index scoring across 4 domains',
      'USSS fast-path for protectee-related reports',
      'Real-time dispatch board with map + live polling',
      'BOLO and agency-routing integration',
    ],
    to: '/subsystems/rerr',
    accent: 'bg-rose-500/10 text-rose-600',
  },
  {
    icon: ShieldCheck,
    code: 'IFPS',
    name: 'Identity Fraud Prevention System',
    tagline: 'Real-time identity challenges coordinated across institutional partners.',
    bullets: [
      'Bank / issuer challenge + response in seconds',
      'Identity-state state machine per individual',
      'Dispute workflow with named reviewers',
      'Escalation board for high-severity events',
    ],
    to: '/subsystems/ifps',
    accent: 'bg-sky-500/10 text-sky-600',
  },
  {
    icon: Wallet,
    code: 'FIRL',
    name: 'Funds Integrity Routing Layer',
    tagline: 'Authoritative objectives + anomaly detection over federal fund flows.',
    bullets: [
      'Federal Fund Objective (FFO) lifecycle',
      'Gate evaluation with deterministic + probabilistic checks',
      'Anomaly board for program-officer review',
      'Enforcement actions with signed decisions',
    ],
    to: '/subsystems/firl',
    accent: 'bg-emerald-500/10 text-emerald-600',
  },
  {
    icon: Vote,
    code: 'VERIS',
    name: 'Voter Eligibility & Registration Integrity',
    tagline: 'Cross-jurisdiction dedup, eligibility states, election-official workflow.',
    bullets: [
      'Deterministic + probabilistic dedup matcher',
      'Eligibility state machine per registrant',
      'Cross-jurisdiction routing + dispute handling',
      'Real-time match broadcasts to election officials',
    ],
    to: '/subsystems/veris',
    accent: 'bg-amber-500/10 text-amber-600',
  },
];

export default function SubsystemsOverview() {
  return (
    <>
      <Hero
        eyebrow="Subsystems"
        title="Four primary subsystems. One integrity contract."
        description="Every subsystem enforces the same guarantees — device-attested intake, role-based review, append-only audit, and signed outputs — so an agency or institution that integrates with one is already aligned with the rest."
      />

      <section className="py-16 md:py-20">
        <div className="container-wide">
          <div className="grid gap-8 lg:grid-cols-2">
            {subsystems.map(s => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.code}
                  to={s.to}
                  className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-8 transition hover:border-slate-300 hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${s.accent}`}>
                      <Icon className="h-6 w-6" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{s.code}</div>
                      <h2 className="text-xl font-semibold text-slate-900">{s.name}</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-base leading-7 text-slate-600">{s.tagline}</p>
                  <ul className="mt-6 space-y-2">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" aria-hidden />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 group-hover:text-sky-700">
                    Read more <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="container-narrow">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Supporting services</h2>
          <p className="mt-3 text-slate-600">
            In addition to the four primary subsystems, NACIS runs a set of shared services that every subsystem
            depends on: the Hub (audit, routing, dashboards), the Cross-Sector Dependency Registry (CSDR), the
            Post-Mortem Identity Containment Service (PMICS), the Credential Dependency Mapping Service (CDMS),
            the Judicial E-Warrant Packaging layer, the Vision analysis pipeline, the OSINT Fusion module, and the
            National-Security Alert Integration (non-DoD).
          </p>
        </div>
      </section>

      <CTABanner
        title="Ready to integrate?"
        description="All integrations run through the Interoperability Sandbox before production, with Safe Entity Attestation and tier-appropriate credentials."
        primaryCta={{ to: '/for-partners', label: 'Partnership tiers' }}
        secondaryCta={{ to: '/contact',     label: 'Talk to us' }}
      />
    </>
  );
}
